import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";

const PHOTOS = [
  {
    id: "team-photo-boardroom",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_mortgage-elite/artifacts/1a2ptk95_StraviaLending_Caitlin_110.jpg",
    alt: "Caitlin Morrison working alongside the AtlasBroker team",
    className: "md:col-span-7 aspect-[3/2]",
  },
  {
    id: "team-photo-working",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_mortgage-elite/artifacts/e9nz6xl4_StraviaLending_Caitlin_139.jpg",
    alt: "Caitlin Morrison working from home",
    className: "md:col-span-5 aspect-[4/3] md:mt-20",
  },
];

const TeamPhoto = ({ photo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <Reveal className={photo.className}>
      <div
        ref={ref}
        className="overflow-hidden rounded-[4px] shadow-[0_4px_40px_-10px_rgba(0,0,0,0.1)] h-full"
        data-testid={`${photo.id}-frame`}
      >
        <motion.img
          src={photo.src}
          alt={photo.alt}
          style={{ y, scale: 1.15 }}
          className="w-full h-full object-cover"
          data-testid={photo.id}
        />
      </div>
    </Reveal>
  );
};

export const Team = () => (
  <section
    id="team"
    data-testid="team-section"
    className="py-32 md:py-48 bg-[#F1EAE1] border-y border-black/[0.06]"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-20 md:mb-28">
        <Reveal className="md:col-span-7">
          <p
            data-testid="team-overline"
            className="text-xs font-semibold tracking-[0.35em] uppercase text-[#CFAEA5] mb-6"
          >
            The Support Behind The Service
          </p>
          <h2
            data-testid="team-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-balance"
          >
            Backed by the award-winning team at{" "}
            <em className="italic text-[#CFAEA5]">AtlasBroker.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-4 md:col-start-9">
          <p
            data-testid="team-body"
            className="text-base leading-relaxed tracking-wide text-[#908B85]"
          >
            The team at AtlasBroker support Caitlin behind the scenes —
            helping her deliver the highest quality customer service and
            advice, and a truly personalised experience for every client.
          </p>
          <a
            href="https://atlasbroker.com.au"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="team-atlasbroker-link"
            className="mt-8 inline-flex flex-col gap-3 group"
          >
            <img
              src="/atlasbroker-logo.svg"
              alt="AtlasBroker"
              data-testid="team-atlasbroker-logo"
              className="h-7 w-auto self-start opacity-80 transition-opacity duration-300 group-hover:opacity-100"
            />
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-[#493E38] border-b border-[#A9ADA1] pb-2 self-start group-hover:text-[#CFAEA5] transition-colors duration-300">
              Visit atlasbroker.com.au
            </span>
          </a>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {PHOTOS.map((photo) => (
          <TeamPhoto key={photo.id} photo={photo} />
        ))}
      </div>
    </div>
  </section>
);
